/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { getApiUrl, getPortalUrl } from "constants/domain"

export const apiReducerPaths = {
  grabXApi: "grabXApi",
  menuApi: "menuApi",
  bulkUploadApi: "bulkUploadApi",
  merchantApi: "merchantApi",
  incidentsApi: "incidentsApi",
  merchantGroupApi: "merchantGroupApi",
} as const

export const apiBaseUrl = `${getApiUrl()}/food/merchant`
export const portalBaseUrl = `${getPortalUrl()}/foodtroy`
export const dpApiMerchantUrl = `${getApiUrl()}/delvplatformapi/merchant`

export const dpApiEndpoints = {
  getDpApiFeatures: "/v1/features",
  searchOutlets: "/v1/merchant-group/store/search",
  fetchItemsWithoutPhotoCount:
    "/v1/merchant-group/store/merchants-items-without-photo-count",
  fetchMerchantAggregate: "/v1/merchant-group/store/aggregate-by-fields",
}

export const endpoints = {
  uploadFile: "/v2/upload-file",
  menuV3EditorStatus: "/v3/menu/editor-status",
  menuV3ModifierGroups: "/v3/menu/modifier-groups",
  menuV3Hierarchies: "/v3/menu/hierarchies",
  menuV3Items: "/v3/menu/items",
  sortDepartmentItems: "/departments/items-sort",
  listSellingTimes: "/v1/menu/selling-times",
  modifierGroupsDetails: "/v1/menu/modifier-groups/details",
  itemDetailsByItemIDs: "/v1/menu/items/details",
  searchItems: "/v1/search-items",
  searchMenusForMex: "/v2/search-menu",
  martItemMenuDisplayV2: "/v2/mart-menu",
  listAttributes: "/v1/attributes",
  deleteItemV2: "/v2/items/bulk",
  updateItemsAvailableV1: "/v1/items/available-status",
  updateItemsSellingTimeV2: "/v2/items/sellingTime",
  getMerchant: "/v2/merchants",
  fetchIncidentList: "/v1/merchant/incidentlist",
  fetchIncidentStat: "/v1/merchant/incidentstats",
}

export const foodTroyEndpoints = {
  getMerchantGroupStores: (countryCode: string, offset = 0, limit = 100) => {
    return `/v1/${countryCode}/merchant-groups/catalog-stores?offset=${offset}&limit=${limit}`
  },
}

export const bulkUploadEndpoints = {
  getBulkTasks: "/v1/bulktasks",
  createBulkTask: "/v1/bulktasks",
  completeBulkTask: (taskID: string) =>
    `/v1/bulktasks/${taskID}/complete-uploading`,
}

// Tag types for RTK Query cache invalidation
export const CACHE_QUERY_TAGS = {
  menuV3Items: "menuV3Items",
  menuV3Hierarchies: "menuV3Hierarchies",
  menuV3ModifierGroups: "menuV3ModifierGroups",
  listSellingTimes: "listSellingTimes",
  searchMenusForMex: "searchMenusForMex",
  martItemMenuDisplayV2: "martItemMenuDisplayV2",
  bulkTasks: "bulkTasks",
} as const

/**
 * RTK Query tags to invalidate when mart partial menu skips a full `fetchCatalogue`
 * (bulk notifications, refresh middleware, etc.).
 */
export const PARTIAL_MENU_REFRESH_TAGS = [
  CACHE_QUERY_TAGS.menuV3Items,
  CACHE_QUERY_TAGS.menuV3Hierarchies,
  CACHE_QUERY_TAGS.menuV3ModifierGroups,
  CACHE_QUERY_TAGS.martItemMenuDisplayV2,
  CACHE_QUERY_TAGS.searchMenusForMex,
]

export const merchantGroupEndpoints = {
  searchStores: dpApiEndpoints.searchOutlets,
} as const

export const MERCHANT_GROUP_CACHE_TAGS = {
  stores: "stores",
} as const
